import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import api from '../lib/api'

const paymentStatusLabel = {
  approved: 'Aprovado',
  pending: 'Pendente',
  rejected: 'Recusado',
  cancelled: 'Cancelado',
  refunded: 'Estornado'
}

export default function ProfilePage({ user, setUser }) {
  const [subscription, setSubscription] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [professionalAppointments, setProfessionalAppointments] = useState([])
  const [wallet, setWallet] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [searchParams] = useSearchParams()

  const isProfessional = user?.userType === 'professional'

  useEffect(() => {
    const paymentReturn = searchParams.get('payment_return')
    if (paymentReturn === 'success') setMessage('Pagamento enviado ao Mercado Pago. A aprovação será refletida automaticamente assim que o webhook confirmar o pagamento.')
    if (paymentReturn === 'pending') setMessage('Pagamento pendente no Mercado Pago. Assim que houver confirmação, sua assinatura será atualizada automaticamente.')
    if (paymentReturn === 'failure') setMessage('O pagamento não foi concluído. Você pode tentar novamente.')
  }, [searchParams])

  const load = async () => {
    if (!user?.id) return
    setLoading(true)
    setError('')
    const requests = [
      api.get(`/subscriptions/status/${user.id}`),
      api.get(`/appointments/${user.id}`),
      api.get('/credits/me')
    ]
    if (isProfessional) requests.push(api.get('/appointments/professional/me'))

    const results = await Promise.allSettled(requests)
    const [subscriptionRes, appointmentsRes, creditsRes, professionalRes] = results

    setSubscription(subscriptionRes?.status === 'fulfilled' ? subscriptionRes.value.data : null)
    setAppointments(appointmentsRes?.status === 'fulfilled' ? (appointmentsRes.value.data.appointments || []) : [])
    setWallet(creditsRes?.status === 'fulfilled' ? (creditsRes.value.data.wallet || null) : null)
    setProfessionalAppointments(professionalRes?.status === 'fulfilled' ? (professionalRes.value.data.appointments || []) : [])

    if (results.every((item) => item.status === 'rejected')) {
      setError('Não foi possível carregar os dados do perfil agora.')
    } else if (results.some((item) => item.status === 'rejected')) {
      setMessage('Parte do perfil foi carregada, mas alguns blocos estão temporariamente indisponíveis.')
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [user?.id, isProfessional])

  const cancelSubscription = async () => {
    try {
      await api.post(`/subscriptions/cancel/${user.id}`, { reason: 'Solicitação feita na área do usuário' })
      const { data } = await api.get(`/subscriptions/status/${user.id}`)
      setSubscription(data)
      setMessage('Assinatura cancelada com sucesso.')
      setError('')
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível cancelar a assinatura agora.')
    }
  }

  const handleStartSession = async (appointmentId) => {
    try {
      setActionLoadingId(`start-${appointmentId}`)
      const { data } = await api.post(`/appointments/${appointmentId}/video-session/start`)
      setMessage(data.message || 'Sessão iniciada com sucesso.')
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível iniciar a sessão agora.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleEndSession = async (appointmentId) => {
    try {
      setActionLoadingId(`end-${appointmentId}`)
      const { data } = await api.post(`/appointments/${appointmentId}/video-session/end`)
      setMessage(data.message || 'Sessão encerrada com sucesso.')
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível encerrar a sessão agora.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
  }

  const latestPayment = subscription?.latestPayment
  const consumerAppointmentsTitle = useMemo(() => isProfessional ? 'Meus atendimentos como usuário' : 'Meus agendamentos', [isProfessional])

  if (loading) return <div className="loading-screen">Carregando perfil...</div>

  return (
    <div>
      <h1 className="text-4xl font-bold mb-8">👤 Meu Perfil</h1>
      {message && <div className="mb-6 rounded bg-green-50 text-green-700 px-4 py-3">{message}</div>}
      {error && <div className="mb-6 rounded bg-red-50 text-red-700 px-4 py-3">{error}</div>}
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6">Informações Pessoais</h2>
          <div className="space-y-4">
            <div><label className="block text-gray-600 font-semibold mb-2">Nome</label><input type="text" value={user.name} className="w-full px-4 py-2 border rounded" disabled /></div>
            <div><label className="block text-gray-600 font-semibold mb-2">Email</label><input type="email" value={user.email} className="w-full px-4 py-2 border rounded" disabled /></div>
            <div><label className="block text-gray-600 font-semibold mb-2">Perfil</label><input type="text" value={user.userType} className="w-full px-4 py-2 border rounded" disabled /></div>
            <button onClick={logout} className="w-full bg-gray-900 text-white px-4 py-2 rounded font-bold">Sair</button>
          </div>
        </div>
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6">Minha Assinatura</h2>
          <div className="space-y-4">
            <div><label className="block text-gray-600 font-semibold mb-2">Plano Atual</label><div className="px-4 py-2 bg-blue-100 text-blue-800 rounded font-bold">{subscription?.plan?.name || 'Sem plano'}</div></div>
            <div><label className="block text-gray-600 font-semibold mb-2">Status</label><div className="px-4 py-2 bg-green-100 text-green-800 rounded font-bold">{subscription?.status || 'inactive'}</div></div>
            <div><label className="block text-gray-600 font-semibold mb-2">Próxima Renovação</label><div className="px-4 py-2 bg-gray-100 rounded">{subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString('pt-BR') : 'Não agendada'}</div></div>
            <button onClick={cancelSubscription} className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-bold">Cancelar Assinatura</button>
          </div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md mt-8">
        <h2 className="text-2xl font-bold mb-6">🪙 Créditos do Portal</h2>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div><span className="font-semibold">Saldo:</span> {wallet?.balance || 0}</div>
          <div><span className="font-semibold">Total ganho:</span> {wallet?.lifetimeEarned || 0}</div>
          <div><span className="font-semibold">Total gasto:</span> {wallet?.lifetimeSpent || 0}</div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md mt-8">
        <h2 className="text-2xl font-bold mb-6">💳 Último Pagamento</h2>
        {!latestPayment ? (
          <p className="text-gray-600">Nenhum pagamento encontrado.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div><span className="font-semibold">Status:</span> {paymentStatusLabel[latestPayment.status] || latestPayment.status}</div>
            <div><span className="font-semibold">Valor:</span> R$ {Number(latestPayment.amount || 0).toFixed(2)}</div>
            <div><span className="font-semibold">Método:</span> {latestPayment.paymentMethod || '—'}</div>
            <div><span className="font-semibold">Referência:</span> {latestPayment.externalReference || '—'}</div>
            <div><span className="font-semibold">ID pagamento MP:</span> {latestPayment.mercadoPagoPaymentId || 'Aguardando'}</div>
            <div><span className="font-semibold">Atualizado em:</span> {latestPayment.updatedAt ? new Date(latestPayment.updatedAt).toLocaleString('pt-BR') : '—'}</div>
            {latestPayment.errorMessage && <div className="md:col-span-2 text-red-600"><span className="font-semibold">Detalhe:</span> {latestPayment.errorMessage}</div>}
          </div>
        )}
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md mt-8">
        <h2 className="text-2xl font-bold mb-6">📅 {consumerAppointmentsTitle}</h2>
        {appointments.length === 0 ? <p className="text-gray-600">Nenhum agendamento encontrado.</p> : (
          <div className="space-y-3">{appointments.map((item) => <div key={item.id} className="border rounded p-4"><p className="font-bold">{item.professionalName}</p><p>{new Date(item.appointmentDate).toLocaleDateString('pt-BR')} às {String(item.appointmentTime).slice(0, 5)}</p><p className="text-sm text-gray-600">Status: {item.status} • Modalidade: {item.modality || 'online'} • Serviço: {item.serviceType || 'anamnese'} • Tempo máximo: {item.durationMinutes} min</p>{item.studentProfile?.studentName ? <p className="text-sm text-gray-600 mt-1">Criança: {item.studentProfile.studentName}</p> : null}{item.intakeForm?.reasonForConsultation ? <p className="text-sm text-gray-600 mt-1">Motivo: {item.intakeForm.reasonForConsultation}</p> : null}<div className="mt-3 flex gap-2 flex-wrap">{item.videoCallLink ? <Link to={`/videochamada/${item.id}`} className="text-blue-700 font-semibold">Entrar na videochamada</Link> : <span className="text-sm text-gray-500">Aguardando o profissional iniciar a sala</span>}{item.meetingId ? <span className="text-sm text-gray-500">Sala: {item.meetingId}</span> : null}</div></div>)}</div>
        )}
      </div>

      {isProfessional ? (
        <div className="bg-white p-8 rounded-lg shadow-md mt-8">
          <h2 className="text-2xl font-bold mb-6">🎥 Sessões que vou conduzir</h2>
          {professionalAppointments.length === 0 ? <p className="text-gray-600">Nenhum atendimento vinculado ao seu perfil profissional.</p> : (
            <div className="space-y-3">
              {professionalAppointments.map((item) => (
                <div key={item.id} className="border rounded p-4">
                  <p className="font-bold">{item.userName}</p>
                  <p>{new Date(item.appointmentDate).toLocaleDateString('pt-BR')} às {String(item.appointmentTime).slice(0, 5)}</p>
                  <p className="text-sm text-gray-600">Status: {item.status} • Serviço: {item.serviceType} • Tempo máximo: {item.durationMinutes} min</p>
                  {item.sessionWindow?.endsAt ? <p className="text-sm text-gray-600 mt-1">Encerra automaticamente em: {new Date(item.sessionWindow.endsAt).toLocaleString('pt-BR')}</p> : null}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {!item.videoCallLink && item.status !== 'completed' ? (
                      <button className="px-4 py-2 rounded bg-blue-600 text-white font-semibold" onClick={() => handleStartSession(item.id)} disabled={actionLoadingId === `start-${item.id}`}>
                        {actionLoadingId === `start-${item.id}` ? 'Iniciando...' : 'Iniciar sessão'}
                      </button>
                    ) : null}
                    {item.videoCallLink ? <Link to={`/videochamada/${item.id}`} className="px-4 py-2 rounded bg-gray-900 text-white font-semibold">Abrir sala</Link> : null}
                    {item.videoCallLink && item.status !== 'completed' ? (
                      <button className="px-4 py-2 rounded bg-red-600 text-white font-semibold" onClick={() => handleEndSession(item.id)} disabled={actionLoadingId === `end-${item.id}`}>
                        {actionLoadingId === `end-${item.id}` ? 'Encerrando...' : 'Encerrar sessão'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
